import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type { BoardColumn } from '@rundown/types';
import { queryKeys } from '../../lib/queryKeys';
import { boardKeys, moveCard, type BoardCard } from './api';

export interface SetColumnVariables {
  readonly requestId: string;
  readonly column: BoardColumn;
}

/**
 * Move one request to a column, from anywhere it is listed.
 *
 * ── Why not `useBoardMove` ──────────────────────────────────────────────────
 * That one is the drag: it is optimistic against the Board's own cache and
 * needs the two neighbours the card was dropped between. A menu has neither —
 * it names a column and nothing about position — and it is used on screens
 * whose cache is not the Board's at all (the Backlog, the Plan columns). So
 * this is the plain version: no optimism, no neighbours, invalidate and refetch.
 *
 * ── Why not `PATCH /requests/:id` ───────────────────────────────────────────
 * The request PATCH accepts `boardColumn`, and using it would have been one
 * fewer endpoint. But the board move is the one that does the whole job: it
 * computes a rank for the card's new column (a PATCH leaves it holding the rank
 * it had in the old one), it writes the richer `request.board_moved` trail
 * entry rather than a generic field change, and it notifies the watchers. Two
 * ways to move a card that behave differently is how the two drift.
 */
export function useSetColumn(workspaceId: string): UseMutationResult<BoardCard, Error, SetColumnVariables> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ requestId, column }) => moveCard(workspaceId, requestId, { column }),
    onSuccess: () => {
      // Named rather than invalidating the whole ['ws', id] subtree: a status
      // change touches the surfaces that list requests, and nothing else. The
      // settings screens, the notifications and the month-end figures have no
      // stake in it and a blanket invalidation would refetch all of them.
      // `requests` is a PREFIX: it catches the open request and its activity
      // tab (['…','requests',id,…]) without naming either — see queryKeys.ts's
      // hard rule about every key starting ['ws', workspaceId].
      const keys = [
        boardKeys.list(workspaceId),
        queryKeys.requests(workspaceId),
        queryKeys.backlog(workspaceId),
        queryKeys.activity(workspaceId),
        ['ws', workspaceId, 'delivery'],
      ];
      for (const queryKey of keys) void queryClient.invalidateQueries({ queryKey });
    },
  });
}
